import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useContext,
} from "react";
import type { ITimelineSettings } from "../types";
import { ensureTimelineComboSettings } from "../utils";

export interface IMarkerSize {
  width: number;
  height: number;
  /** Marker center X relative to its grid-cell left edge (px). Used by
   *  the path / snake overlays so mask cuts track per-item marker positions. */
  xOffsetInCell?: number;
}

export interface ITimelineCtx {
  comboId: string;
  isPdf: boolean;
  isMobile: boolean;
  totalAmtOfItems: number;
  itemsInView: number;
  isVertical: boolean;
  isHorizontal: boolean;
  itemsMovedBy: number;
  setMovedBy: Dispatch<SetStateAction<number>>;
  comboSettings: ITimelineSettings;
  /** Marker bounds keyed by global itemIdx, populated by each track
   *  segment via ResizeObserver. Consumed by the path sinusoid + snake
   *  overlays to size mask cut-outs around each marker. */
  markerSizes: Map<number, IMarkerSize>;
  registerMarkerSize: (idx: number, size: IMarkerSize) => void;
  /** Per-item pos-0 / pos-1 heights for the split-middle layout. Pinning
   *  both to the max keeps the track line vertically centered across
   *  every item. */
  pos0Heights: Map<number, number>;
  registerPos0Height: (idx: number, height: number) => void;
  pos1Heights: Map<number, number>;
  registerPos1Height: (idx: number, height: number) => void;
}

const initState: ITimelineCtx = {
  comboId: "",
  isPdf: false,
  isMobile: false,
  isVertical: true,
  isHorizontal: false,
  totalAmtOfItems: 4,
  itemsInView: 4,
  itemsMovedBy: 0,
  setMovedBy: () => 0,
  comboSettings: ensureTimelineComboSettings(),
  markerSizes: new Map(),
  registerMarkerSize: () => {},
  pos0Heights: new Map(),
  registerPos0Height: () => {},
  pos1Heights: new Map(),
  registerPos1Height: () => {},
};

export const TimelineCtx = createContext<ITimelineCtx>(initState);

interface IProps {
  children: ReactNode;
  ctxState: ITimelineCtx;
}

export const TimelineCtxProvider = ({ children, ctxState }: IProps) => {
  return (
    <TimelineCtx.Provider value={ctxState}>{children}</TimelineCtx.Provider>
  );
};

export const useTimelineCtx = () => {
  const ctx = useContext(TimelineCtx);
  if (!ctx) throw Error("useTimelineCtx: TimelineCtx is not defined!");

  const moveToItemIdx = (itemIdx: number) => {
    const { itemsInView, totalAmtOfItems, setMovedBy } = ctx;
    setMovedBy(Math.max(0, Math.min(totalAmtOfItems - itemsInView, itemIdx)));
  };

  const moveToPrevView = () => {
    const { itemsInView, totalAmtOfItems, setMovedBy } = ctx;

    setMovedBy((prev) =>
      Math.max(0, Math.min(totalAmtOfItems - itemsInView, prev - itemsInView)),
    );
  };

  const moveToNextView = () => {
    const { itemsInView, totalAmtOfItems, setMovedBy } = ctx;
    setMovedBy((prev) =>
      Math.max(0, Math.min(totalAmtOfItems - itemsInView, prev + itemsInView)),
    );
  };

  return { ...ctx, moveToItemIdx, moveToNextView, moveToPrevView };
};
